import { all, allPass, clone, compose, concat, curry, has, join, pathOr, propSatisfies, split, takeLast, uniq } from 'ramda'
import { isArray, isObject } from 'ramda-adjunct'
import { getCookie, removeCookie, setCookie } from './cookie'


/**
 * For some strange reason, we cannot use the ramda version of `is`
 * because it fails on the server side
 *
 * @sig (* -> {*}) -> a -> Boolean
 * @see https://ramdajs.com/docs/#is
 */
const is = curry((Ctor, val) => val != null && val.constructor === Ctor || val instanceof Ctor) // eslint-disable-line

// parseJson :: a -> Obj | a
export const parseJson = (input, defaultValue = null) => {
    try {
        return JSON.parse(input)
    } catch (_) {
        return defaultValue || input
    }
}

// getAvailableCookieIds :: Obj -> [String]
export const getAvailableCookieIds = cookieConfig => clone(cookieConfig.cookies.map(x => x.id))

// getTechnicallyRequiredCookieIds :: Obj -> [String]
export const getTechnicallyRequiredCookieIds = cookieConfig => clone(cookieConfig.cookies.filter(cookie => cookie.technicallyRequired).map(x => x.id))

export const getSecondLevelDomain = compose(
    join('.'),
    takeLast(2),
    split('.'),
)

export const getPathsFromLocation = (locationPath) => {
    const pathname = locationPath.replace(/\/$/, '')
    const segments = pathname.split('/')
    const paths = []

    for (let i = 0; i < segments.length; i += 1) {
        const path = segments.slice(0, i + 1).join('/')
        paths.push(path, `${path}/`)
    }

    return paths
}

/**
 * Cookies can only be removed if you know their domain and path.
 * @see https://stackoverflow.com/a/2421786
 *
 * Hence we try all possible combinations of paths and domains for deleting old cookies
 *
 * @see https://stackoverflow.com/questions/7487210/removing-cookies-with-the-same-name-but-different-paths/7487448#7487448
 *
 * Example www.snoopmedia.com:
 * When saving a cookie, without specifying a domain, the cookie will be saved with domain "www.snoopmedia.com"
 * Google Analytics on the other hand saves its cookies with the domain ".snoopmedia.com".
 *
 * @sig String -> [String] -> [String] -> *
 */
const removeCookieByPathsAndDomains = (cookieName, paths, domains) => {
    domains.forEach(domain => paths.forEach((path) => {
        removeCookie(cookieName, path, domain)
        if (domain) {
            removeCookie(cookieName, path, `.${domain}`)
        }
    }))
}

/**
 * Remove all cookies that were not accepted
 * @param cookieConfig {object}
 * @param acceptedCookieIds {array}
 * @private
 */
const removeNotAcceptedCookies = (cookieConfig, acceptedCookieIds) => {
    const notAcceptedCookies = cookieConfig.cookies.filter(cookie => !acceptedCookieIds.includes(cookie.id))

    const domains = uniq(concat(['', window.location.host, getSecondLevelDomain(window.location.host)], cookieConfig.domains || []))
    const paths = concat(cookieConfig.paths || [], getPathsFromLocation(window.location.pathname))

    notAcceptedCookies.forEach((cookieData) => {
        cookieData.cookies.forEach(({ name, path = '/', domain = '' }) => {
            removeCookie(name, path, domain)
            removeCookieByPathsAndDomains(name, paths, domains)
        })
    })
}

// propIsArrayOfString :: String -> Obj -> Boolean
const propIsArrayOfString = curry((propName, obj) => propSatisfies(allPass([
    isArray,
    all(is(String)),
]), propName, obj))

// validateUserSettings :: a -> Boolean
const validateUserSettings = allPass([
    isObject,
    has('acceptedCookies'),
    propIsArrayOfString('acceptedCookies'),
    has('availableCookies'),
    propIsArrayOfString('availableCookies'),
])

/**
 * Helper function to read the state of the cookie consent modal
 * on the server.
 *
 * This may be useful for apps with server side rendering to
 * reduce the largest contentful paint.
 *
 * sig Obj -> String -> { acceptedCookies, isNew, needsRefresh }
 */
export const readUserSettings = (cookieConfig, cookieValue) => {
    const settings = {
        acceptedCookies: [],
        isNew: false,
        needsRefresh: false,
    }

    // Check if cookie holds a valid settings object
    const parsedValue = parseJson(cookieValue, {})
    if (!settings.isNew && !validateUserSettings(parsedValue)) {
        // We dont want to show a message, so lets pretend its a new user.
        settings.isNew = true
    }

    if (!settings.isNew && !settings.needsRefresh) {
        // New cookie layer was already accepted
        const { acceptedCookies = [], availableCookies = [] } = parsedValue
        settings.acceptedCookies = acceptedCookies

        // Now check if new cookies were added since the user's last visit
        const availableCookieIds = getAvailableCookieIds(cookieConfig)
        availableCookieIds.forEach((cookieId) => {
            if (!availableCookies.includes(cookieId)) {
                // There are new cookies, so open modal
                settings.needsRefresh = true
            }
        })
    }

    return settings
}

/**
 * @returns {{acceptedCookies: array, needsRefresh: boolean, isNew: boolean}}
 */
export const readSettingsFromCookies = (cookieConfig) => {
    const cookieValue = getCookie(cookieConfig.consentCookie.name)

    return readUserSettings(cookieConfig, cookieValue)
}

/**
 * Remove the consent cookies before writing. We have to do this because
 * the user may have two cookies with the same name but different domains
 * (from earlier deployments). In this case the browser decides which
 * cookie to handle to the user which may result in an open cookie layer
 * on each page refresh :/ (because we always write into the cookie with a
 * specific domain).
 */
const removeConsentCookie = (cookieName, cookieDomain) => {
    const domains = uniq([
        '',
        cookieDomain,
        window.location.host,
        getSecondLevelDomain(window.location.host),
    ])
    const paths = uniq([
        '/',
        ...getPathsFromLocation(window.location.pathname),
    ])

    removeCookieByPathsAndDomains(cookieName, paths, domains)
}

export const writeUserSettings = (cookieConfig, acceptedCookieIds) => {
    const settings = {
        availableCookies: getAvailableCookieIds(cookieConfig),
        acceptedCookies: acceptedCookieIds,
        lastUpdated: Date.now(),
    }

    const { name, expiresAfterDays, sameSite, domain } = cookieConfig.consentCookie
    removeConsentCookie(name, domain)
    setCookie(name, JSON.stringify(settings), expiresAfterDays, sameSite, domain)

    removeNotAcceptedCookies(cookieConfig, acceptedCookieIds)
}


// getCookieFromNuxtRequest :: String -> Obj -> Sting
const getCookieFromNuxtRequest = (cookieName, req) => {
    const strCookie = pathOr(null, [0], new RegExp(`${cookieName}[^;]+`).exec(req.headers.cookie))

    return strCookie ? decodeURI(strCookie.toString().replace(/^[^=]+./, '')) : null
}

/**
 *
 * @sig Request -> {acceptedCookies: array, needsRefresh: boolean, isNew: boolean}
 */
export const readUserSettingsFromNuxtHeaders = (cookieConfig, req) => {
    if (process.client) return null

    const cookieValue = getCookieFromNuxtRequest(cookieConfig.consentCookie.name, req)
    const settings = readUserSettings(cookieConfig, cookieValue)

    return settings
}
